/**
 * The route path constants. ONE spec per registered route; `router.tsx` builds
 * the route tree from them and `appLinks.ts` builds native destinations from
 * them, so a path exists in exactly one place.
 */
export interface CanonicalRouteSpec<TanStackPath extends string = string> {
  readonly path: TanStackPath;
}

function defineRoute<const TanStackPath extends string>(path: TanStackPath): CanonicalRouteSpec<TanStackPath> {
  return { path };
}

export const canonicalRouteSpecs = {
  login: defineRoute('/login'),
  landing: defineRoute('/'),
  notAMember: defineRoute('/not-a-member'),
  customerPortalProducts: defineRoute('/products'),
  purchaseOrders: defineRoute('/purchase-orders'),
  purchaseOrder: defineRoute('/purchase-orders/$slug'),
  customerPortalInvoices: defineRoute('/invoices'),
  viewCustomerPortalInvoice: defineRoute('/invoices/$slug'),
  settings: defineRoute('/settings'),
  searchResults: defineRoute('/search-results'),
  salesOrders: defineRoute('/sales-orders'),
  salesOrder: defineRoute('/sales-orders/$slug'),
  aiInbox: defineRoute('/ai-inbox/{-$threadId}'),
  buySellOrder: defineRoute('/buy-sell-orders/$slug'),
  growerProducts: defineRoute('/grower-products'),
  growerProduct: defineRoute('/grower-products/$slug'),
  accountingReports: defineRoute('/accounting/reports'),
  accounts: defineRoute('/accounting/accounts'),
  accountRegister: defineRoute('/accounting/accounts/register/{-$path}'),
  invoices: defineRoute('/accounting/invoices'),
  payables: defineRoute('/accounting/payables'),
  bill: defineRoute('/accounting/bill/$slug'),
  payments: defineRoute('/accounting/payments'),
  printChecks: defineRoute('/accounting/payments/print-checks'),
  payment: defineRoute('/accounting/payments/$slug'),
  agingReports: defineRoute('/accounting/aging-reports'),
  inventory: defineRoute('/inventory'),
  productionRuns: defineRoute('/inventory/production-runs'),
  productionRun: defineRoute('/inventory/production-runs/$slug'),
  viewPickTickets: defineRoute('/inventory/pick-tickets'),
  editPickTicket: defineRoute('/inventory/pick-tickets/$slug'),
  viewReceivingTickets: defineRoute('/inventory/receiving-tickets'),
  editReceivingTicket: defineRoute('/inventory/receiving-tickets/$slug'),
  viewLotTracking: defineRoute('/inventory/lot-tracking/$warehouseId/$slug'),
  inventoryTransfer: defineRoute('/inventory/transfers/$slug'),
  inventoryTransfers: defineRoute('/inventory/transfers'),
  settingsAccounting: defineRoute('/settings/accounting'),
  settingsBusiness: defineRoute('/settings/business'),
  settingsEntities: defineRoute('/settings/business/entities/$id'),
  pricingSheet: defineRoute('/settings/business/pricing-sheet/$id'),
  settingsAutomations: defineRoute('/settings/automations'),
  settingsEmails: defineRoute('/settings/emails'),
  settingsOrders: defineRoute('/settings/orders'),
  settingsDocuments: defineRoute('/settings/documents'),
  settingsPortal: defineRoute('/settings/portal'),
  settingsInventory: defineRoute('/settings/inventory'),
  warehouseDetails: defineRoute('/settings/inventory/warehouses/$warehouseId'),
  settingsTeam: defineRoute('/settings/team'),
  documentEditor: defineRoute('/internal/documents/editor'),
  palletTags: defineRoute('/internal/pallet-tags'),
  palletTagEditor: defineRoute('/internal/pallet-tags/editor'),
  userProfile: defineRoute('/user-profile'),
  businessIntelligence: defineRoute('/business-intelligence'),
  biDashboards: defineRoute('/business-intelligence/$slug'),
  orderBoard: defineRoute('/order-board'),
  scan: defineRoute('/scan'),
  scanLocation: defineRoute('/scan/location'),
  recipeBuilder: defineRoute('/internal/recipe-builder/{-$slug}'),
  scanDebug: defineRoute('/scan-debug'),
};
